import { useRecordContext } from "react-admin";
import {
  Box,
  Button,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { settings } from "app";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ConfirmModal from "server/components/Modals/ConfirmModal";
import { formatDecimal } from "./utils";

const MIN_GRADE = 2;
const MAX_GRADE = 6;

// Red (2.00) -> amber -> green (6.00), so the scale reads at a glance.
const gradeColor = (grade, opacity = 1) => {
  const ratio = Math.min(Math.max((grade - MIN_GRADE) / (MAX_GRADE - MIN_GRADE), 0), 1);
  return `hsl(${Math.round(ratio * 120)} 65% 40% / ${opacity})`;
};

// Thresholds come sorted by minPoints; each band ends just below the next one's minimum.
const toBands = ({ gradeThresholds = [], maxPoints }) =>
  gradeThresholds.map((threshold, index) => ({
    grade: threshold.grade,
    from: threshold.minPoints,
    to: index < gradeThresholds.length - 1 ? gradeThresholds[index + 1].minPoints - 0.01 : maxPoints,
  }));

const GradeBand = ({ grade, from, to, maxPoints }) => {
  const percent = value => (maxPoints ? (value / maxPoints) * 100 : 0);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        px: 2,
        py: 1.25,
        border: 1,
        borderColor: "divider",
        borderRadius: 2,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: 64,
          py: 0.75,
          borderRadius: 1.5,
          textAlign: "center",
          fontWeight: 700,
          fontSize: 18,
          color: gradeColor(grade),
          bgcolor: gradeColor(grade, 0.12),
        }}
      >
        {grade.toFixed(2)}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, mb: 0.75 }}
        >
          {`${formatDecimal(from)} – ${formatDecimal(to)} т.`}
        </Typography>
        {/* Where the band sits on the whole 0..maxPoints scale. */}
        <Box sx={{ position: "relative", height: 6, borderRadius: 3, bgcolor: "action.hover" }}>
          <Box
            sx={{
              position: "absolute",
              top: 0,
              bottom: 0,
              left: `${percent(from)}%`,
              width: `${Math.max(percent(to) - percent(from), 1)}%`,
              borderRadius: 3,
              bgcolor: gradeColor(grade),
            }}
          />
        </Box>
      </Box>
    </Box>
  );
};

// The old points/grade table, for tenants without the new UI (settings.style.modernUi).
const LegacyGradesTable = ({ record }) => (
  <TableContainer
    component={Paper}
    sx={{ boxShadow: 3, maxHeight: 400, overflowY: "auto" }}
  >
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: theme => theme.palette.secondary.main }}>
          <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Точки</TableCell>
          <TableCell sx={{ fontWeight: "bold", color: "#fff" }}>Оценка</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {toBands(record).map(band => (
          <TableRow key={band.from}>
            <TableCell>{`${formatDecimal(band.from)}т. - ${formatDecimal(band.to)}т.`}</TableCell>
            <TableCell>{band.grade.toFixed(2)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

const GradesPopup = ({ label = "Детайли" }) => {
  const record = useRecordContext();

  if (!record) {
    return null;
  }

  return (
    <ConfirmModal
      triggerButton={<Button startIcon={<VisibilityIcon />}>{label}</Button>}
      titleContent={`Скала: ${record.name}`}
      subtitle={settings.style.modernUi ? `Максимум ${formatDecimal(record.maxPoints)} точки` : undefined}
      isConfirmButton={false}
    >
      {!settings.style.modernUi ? (
        <LegacyGradesTable record={record} />
      ) : (
        <Stack
          spacing={1}
          sx={{ py: 2, maxHeight: 420, overflowY: "auto" }}
        >
          {toBands(record).map(band => (
            <GradeBand
              key={band.from}
              {...band}
              maxPoints={record.maxPoints}
            />
          ))}
        </Stack>
      )}
    </ConfirmModal>
  );
};

export default GradesPopup;
